import { IRepresentation } from 'API';
import { Representation } from 'Enum';
import {
  textTooLong,
  onlyNumbersMessage,
  emailMessage,
  lettersOrNumbersNoSpecialCharsMessage,
  requiredMessage,
  phoneNumberMessage,
  phoneNumberNotRequired,
  lettersOrNumbersNoSpecialChars,
  validateIsraeliIdMessage,
  isValidIsraeliID,
  emailRegexStrict,
} from 'Regex';
import * as yup from 'yup';

export const representationSchema: yup.SchemaOf<IRepresentation> = yup
  .object()
  .shape({
    formFillingAssistant: yup
      .object({
        isAssistedByGoodHands: yup.boolean().notRequired().nullable(),
        isAssistedByIDF: yup.boolean().notRequired().nullable(),
        isAssistedByPolice: yup.boolean().notRequired().nullable(),
        isAssistedByPrisons: yup.boolean().notRequired().nullable(),
        isAssistedByRam2: yup.boolean().notRequired().nullable(),
        none: yup.boolean().notRequired().nullable(),
      })
      .required()
      .test(requiredMessage, function (value) {
        const {
          isAssistedByGoodHands,
          isAssistedByIDF,
          isAssistedByPolice,
          isAssistedByPrisons,
          isAssistedByRam2,
          none,
        } = value || {};
        return !!(
          isAssistedByGoodHands ||
          isAssistedByIDF ||
          isAssistedByPolice ||
          isAssistedByPrisons ||
          isAssistedByRam2 ||
          none
        );
      }),
    associationSection: yup.object({}).when('representationOptionRelationId', {
      is: (val: any) => val == Representation.isAssociation,
      then: yup.object({
        associationName: yup
          .string()
          .max(20, textTooLong(20))
          .required(requiredMessage)
          .matches(
            lettersOrNumbersNoSpecialChars,
            lettersOrNumbersNoSpecialCharsMessage
          )
          .nullable(),
        representativeName: yup
          .string()
          .max(30, textTooLong(30))
          .required(requiredMessage)
          .matches(
            lettersOrNumbersNoSpecialChars,
            lettersOrNumbersNoSpecialCharsMessage
          )
          .nullable(),
        address: yup
          .string()
          .required(requiredMessage)
          .max(50, textTooLong(50))
          .matches(
            lettersOrNumbersNoSpecialChars,
            lettersOrNumbersNoSpecialCharsMessage
          )
          .nullable(),
        phonePrefixRelationId: yup
          .string()
          .required(requiredMessage)
          .nullable(),
        phone: yup
          .string()
          .max(7, textTooLong(7))
          .required(requiredMessage)
          .matches(phoneNumberNotRequired, onlyNumbersMessage)
          .nullable(),
        mail: yup
          .string()
          .max(50, textTooLong(50))
          .matches(emailRegexStrict, emailMessage)
          .required(requiredMessage)
          .nullable(),
      }),
      otherwise: yup.object({}).nullable(),
    }),

    guardianSection: yup
      .object({})
      .when('isRepresentedByGuardian', {
        is: (val: any) => val,
        then: yup.object({
          firstName: yup
            .string()
            .max(20, textTooLong(20))
            .required(requiredMessage)
            .matches(
              lettersOrNumbersNoSpecialChars,
              lettersOrNumbersNoSpecialCharsMessage
            )
            .nullable(),
          lastName: yup
            .string()
            .max(30, textTooLong(30))
            .required(requiredMessage)
            .matches(
              lettersOrNumbersNoSpecialChars,
              lettersOrNumbersNoSpecialCharsMessage
            )
            .nullable(),
          idNumber: yup
            .string()
            .required(requiredMessage)
            .test('isValidIsraeliID', validateIsraeliIdMessage, (value: any) =>
              value === '' ? true : isValidIsraeliID(value)
            )
            .nullable(),
          address: yup
            .string()
            .required(requiredMessage)
            .max(50, textTooLong(50))
            .matches(
              lettersOrNumbersNoSpecialChars,
              lettersOrNumbersNoSpecialCharsMessage
            )
            .nullable(),
          phonePrefixRelationId: yup
            .string()
            .required(requiredMessage)
            .nullable(),
          phone: yup
            .string()
            .max(7, textTooLong(7))
            .required(requiredMessage)
            .matches(phoneNumberNotRequired, onlyNumbersMessage)
            .nullable(),
          mail: yup
            .string()
            .max(50, textTooLong(50))
            .matches(emailRegexStrict, emailMessage)
            .required(requiredMessage)
            .nullable(),
        }),
        otherwise: yup.object({}),
      })
      .nullable(),
    lawyerSection: yup
      .object({})
      .when('representationOptionRelationId', {
        is: (val: any) => val == Representation.isLawyer,
        then: yup.object({
          firstName: yup
            .string()
            .max(20, textTooLong(20))
            .required(requiredMessage)
            .matches(
              lettersOrNumbersNoSpecialChars,
              lettersOrNumbersNoSpecialCharsMessage
            )
            .nullable(),
          lastName: yup
            .string()
            .max(30, textTooLong(30))
            .required(requiredMessage)
            .matches(
              lettersOrNumbersNoSpecialChars,
              lettersOrNumbersNoSpecialCharsMessage
            )
            .nullable(),
          address: yup
            .string()
            .required(requiredMessage)
            .max(50, textTooLong(50))
            .matches(
              lettersOrNumbersNoSpecialChars,
              lettersOrNumbersNoSpecialCharsMessage
            )
            .nullable(),
          phoneNum: yup
            .string()
            .required(requiredMessage)
            .matches(phoneNumberNotRequired, phoneNumberMessage)
            .nullable(),
        }),
        otherwise: yup.object({
          firstName: yup
            .string()
            .max(20, textTooLong(20))
            .notRequired()
            .matches(
              lettersOrNumbersNoSpecialChars,
              lettersOrNumbersNoSpecialCharsMessage
            )
            .nullable(),
          lastName: yup
            .string()
            .max(30, textTooLong(30))
            .notRequired()
            .matches(
              lettersOrNumbersNoSpecialChars,
              lettersOrNumbersNoSpecialCharsMessage
            )
            .nullable(),
          address: yup
            .string()
            .notRequired()
            .max(50, textTooLong(50))
            .matches(
              lettersOrNumbersNoSpecialChars,
              lettersOrNumbersNoSpecialCharsMessage
            )
            .nullable(),
          phoneNum: yup.string().notRequired().nullable(),
        }),
      })
      .nullable(),
    isRepresentedByGuardian: yup.boolean().required(requiredMessage).nullable(),
    representationOptionRelationId: yup
      .string()
      .required(requiredMessage)
      .nullable(),
  });
